/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './Settings.css'; // Import the CSS file
import Image from './Image';
import { PlayRound, Trash, Spinner, CirclePlusIcon } from '../../LandingHeader/Feedback/icons/Icons';
import { setBackgroundImages, setDarkThemeDefaultImage, setThemeDefaultImage } from '../../../redux/slices/ThemeSlice';
import { deleteBackgroundImage, setDefaultAccentColor, setDefaultBackgroundImage, setSetting, uploadBackgroundImage } from '../../action/common';
import { fetchBackgroundImages } from '../../action/utilities';
import { pushToSetting } from '../../../redux/slices/SettingSclice';
import { extractColors } from "extract-colors";

function AdminSettings({ activeTab, setActiveTab }) {
    const { theme, colorPicker, colorPickerDark, backgroundImages } = useSelector(state => state.theme);
    const { settings } = useSelector(state => state.settings);
    const [isUploading, setIsUploading] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        fetchBackgroundImages().then(images => {
            dispatch(setBackgroundImages(images));
        });
    }, []);

    const handleImageUpload = async (event, isDarkMode = false) => {
        const file = event.target.files[0];
        if (file) {
            setIsUploading(isDarkMode ? 'dark' : 'light');
            try {
                const formData = new FormData();
                // Pass the local file URL to extractColors instead of the file object
                const colors = await extractColors(URL.createObjectURL(file));
                formData.append("files", file);
                formData.append("systemDefault", true);
                formData.append("palette", colors.map(color => color.hex));
                formData.append("darkMode", isDarkMode);
                const resp = await uploadBackgroundImage(formData);
                const images = [...backgroundImages, resp.data.responseData];
                dispatch(setBackgroundImages(images));
                if (isDarkMode) {
                    document.getElementById('new-image-upload-dark').value = '';
                } else {
                    document.getElementById('new-image-upload').value = '';
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsUploading(false);
            }
        }
    };

    // delete image
    const handleDeleteImage = async (id) => {
        const images = backgroundImages.filter(image => image.id != id);
        deleteBackgroundImage(id);
        dispatch(setBackgroundImages(images));
    };

    // set default image
    const handleDefaultImage = async (image, isDarkMode = false) => {
        let newSettings = { ...settings };
        const images = backgroundImages.map(img => img.id == image.id ? {...img, isDefault: 1} : {...img, isDefault: (img.darkMode == isDarkMode ? 0 : img.isDefault)});
        setDefaultBackgroundImage(image.id, isDarkMode)
        dispatch(setBackgroundImages(images)) 
        if(isDarkMode){
            dispatch(setDarkThemeDefaultImage(image.id))
            newSettings = { ...newSettings, darkBackgroundImageId: image.id };
        }else{
            dispatch(setThemeDefaultImage(image.id))
            newSettings = { ...newSettings, backgroundImageId: image.id };
        }

        dispatch(pushToSetting(newSettings));
        await setSetting({ settings: newSettings });
    };

    const setDefaultColor = (color, imageId) => {
        const images = backgroundImages.map(img => img.id == imageId ? {...img, defaultColor: color} : {...img, defaultColor: null}); 
        setDefaultAccentColor(imageId, color);
        dispatch(setBackgroundImages(images));
    }

    return (
        <li
        css={{
            '&:hover .accordion-title': {
            color: theme == "dark" ? (((theme == "dark" && colorPickerDark == "#ef233c")) ? "white !important" : (theme == "dark" && colorPickerDark == "#f8961e") ? "black !important" : (theme == "dark" && colorPickerDark == "#ffca3a") ? "black !important" : (theme == "dark" && colorPickerDark == "#27cb9a") ? "black !important" : (theme == "dark" && colorPickerDark == "#4cc9f0") ? "black !important" : "white !important") : "white !important"
            },
            '.accordion-title': {
            color: theme == "dark" ? (((theme == "dark" && colorPickerDark == "#ef233c") && (activeTab == "background")) ? "white !important" : ((theme == "dark" && colorPickerDark == "#f8961e") && (activeTab == "background")) ? "black !important" : ((theme == "dark" && colorPickerDark == "#ffca3a") && (activeTab == "background")) ? "black !important" : ((theme == "dark" && colorPickerDark == "#27cb9a") && (activeTab == "general")) ? "black !important" : ((theme == "dark" && colorPickerDark == "#4cc9f0") && (activeTab == "background")) ? "black !important" : "white !important") : "white"
            }
        }}
        className={`li-setting-tab ${activeTab == "background" ? 'active' : ''}`}>
            <div className="settings-accordion-tab dark:bg-black"
                onClick={() => { setActiveTab(activeTab => activeTab == 'background' ? null : 'background'); }}
                css={{
                    background: activeTab == 'background' ? `linear-gradient(0deg, var(--accent-color), var(--accent-color)), var(--theme-mode-color-white) !important` :
                        '',
                    '&:hover': {
                        background: `linear-gradient(0deg, var(--accent-color), var(--accent-color)), var(--theme-mode-color-white) !important`,
                    }
                }}>
                <div className="settings-accordion-heading">
                    <figure className="settings-icon">
                        <img src="/images/general.svg" className="img-default dark:invert-color" />
                    </figure>
                    <h3 className="accordion-title uppercase dark:text-white">Appearance</h3>
                </div>
                <div className="settings-accordion-arrow">
                    <img src="/images/arrow-down.svg" className="dark:invert-color" />
                </div>
            </div>
            {
                 activeTab == 'background' && <div className="settings-accordion-content">
                 <div className="inner-form">
                     <div className='admin-settings-section'>
                         <div className='admin-settings-section-item'>
                             <label>Light mode background images</label>
                         </div>
                         <div className='image-gallery'>
                             {backgroundImages.map((image, index) => (
                                 image.systemDefault && !image?.darkMode ? <div key={image.id} className="image-card-wrapper">
                                     <Image imageObject={image} location={image.location} colorPicker={image?.isDefault ? image?.defaultColor : null} onColorChange={color => setDefaultColor(color, image.id)} isAdmin={true} theme="light" />
                                     <button title={image.location} className="delete-icon" onClick={() => handleDeleteImage(image.id)}>
                                         <Trash color='red' />
                                     </button>
                                     <button className="default-icon" onClick={() => handleDefaultImage(image, false)} style={{background: image?.isDefault ? 'rgba(255,0,0,.7)' : 'none'}}>
                                         <PlayRound color={image?.isDefault ? 'red' : 'black'} />
                                     </button>
                                 </div> : null
                             ))}
                             <div className='image-card new-image-card'>
                                 <label htmlFor="new-image-upload">
                                     {isUploading === 'light' ? (
                                         <>
                                             <Spinner /> <span className='accent-text-color'>Uploading...</span>
                                         </>
                                     ) : (
                                         <>
                                             <CirclePlusIcon color={`var(--accent-color)`} />
                                             <span className='accent-text-color'>Upload Image</span>
                                         </>
                                     )}
                                 </label>
                                 <input 
                                     id="new-image-upload" 
                                     type="file" 
                                     onChange={handleImageUpload} 
                                     disabled={isUploading === 'light'}
                                 />
                             </div>
                         </div>
                     </div>

                     <div className='admin-settings-section mt-10'>
                         <div className='admin-settings-section-item'>
                             <label>Dark mode background images</label>
                         </div>
                         <div className='image-gallery'>
                             {backgroundImages.map((image, index) => (
                                 image.systemDefault && image?.darkMode ? <div key={image.id} className="image-card-wrapper">
                                     <Image imageObject={image} location={image.location} colorPicker={image?.isDefault ? image?.defaultColor : null} onColorChange={color => setDefaultColor(color, image.id)} isAdmin={true} theme="dark" />
                                     <button title={image.location} className="delete-icon" onClick={() => handleDeleteImage(image.id)}>
                                         <Trash color='red' />
                                     </button>
                                     <button className="default-icon" onClick={() => handleDefaultImage(image, true)} style={{background: image?.isDefault ? 'rgba(255,0,0,.7)' : 'none'}}>
                                         <PlayRound color={image?.isDefault ? 'red' : 'black'} />
                                     </button>
                                 </div> : null
                             ))}
                             <div className='image-card new-image-card'>
                                 <label htmlFor="new-image-upload-dark">
                                     {isUploading === 'dark' ? (
                                         <>
                                             <Spinner /> <span className='accent-text-color'>Uploading...</span>
                                         </>
                                     ) : (
                                         <>
                                             <CirclePlusIcon color={`var(--accent-color)`} />
                                             <span className='accent-text-color'>Upload Image</span>
                                         </>
                                     )}
                                 </label>
                                 <input 
                                     id="new-image-upload-dark" 
                                     type="file" 
                                     onChange={e => handleImageUpload(e, true)} 
                                     disabled={isUploading === 'dark'}
                                 />
                             </div>
                         </div>
                     </div>
                 </div>
             </div>
            }
        </li>
    );
}

export default AdminSettings;